import { RequestCooperationByClientSuccessActionParams } from '../actions/freelancers/freelancer-accept-popup';
import {
    UpdateFreelancerCardPrivateDataActionParams,
    UpdateFreelancerCustomerDataActionParams
} from '../actions/freelancers/freelancer-card';
import { UpdateSingleFreelancerDataActionParams } from '../actions/freelancers/freelancers-list';
import { FreelancerAccessError, FreelancerAccessErrorTypes } from '../service/FreelancerAccessError';
import { Lang } from '../typings/common';
import {
    Freelancer,
    FreelancerCardEditData,
    FreelancerCardInitRegion,
    FreelancerCardOperator,
    FreelancerContactInputs,
    FreelancerContacts,
    FreelancerCustomersData,
    FreelancerFeedbackItem,
    FreelancerMandatoryContactTypes,
    FreelancerOptionalContactTypes,
    FreelancersCertificates,
    FreelancersCertificatesTypes,
    FreelancerServiceInput,
    FreelancerSkills,
    FreelancersListItem,
    UpdateActiveProjectData,
    UpdateCustomerRequestData
} from '../typings/freelancers';
import {
    ClientFreelancerQuery,
    CurrencyCode,
    FreelancerContactsFragment,
    FreelancerCustomersQuery,
    FreelancerEditorQuery,
    FreelancerFeedbackQueryFragment,
    FreelancerPrivateDataFragment_newestCard,
    FreelancerRegisteredQuery,
    FreelancerServiceFragment,
    GdFreelancerProjectStatus,
    GdFreelancersCardStatusModerate,
    GdFreelancerSkillOfferDuration,
    GdLanguageInput,
    GdUpdateFreelancerCardItemInput,
    GdUpdateFreelancerSkillItemInput,
    RequestFlServiceByClientMutation
} from '../typings/graphql-grid';
import {
    FreelancerPublicQuery_freelancersList,
    FreelancerPublicQuery_operator,
    FreelancersListItemFragment,
    FreelancersListItemFragment_skills
} from '../typings/graphql-public-grid';
import { ClientFreelancerData } from '../typings/state';

import { getValueFromApiFormat } from './common/common';

export function mapFreelancerPublicResponse(freelancer: FreelancerPublicQuery_freelancersList): Freelancer {
    return {
        freelancerId: String(freelancer.freelancerId),
        login: freelancer.login,
        cardModerationStatus: GdFreelancersCardStatusModerate.ACCEPTED,
        workCurrency: CurrencyCode[freelancer.workCurrency],
        avatarLink: freelancer.card ? freelancer.card.avatarUrl : undefined,
        about: freelancer.card ? freelancer.card.briefInfo : undefined,
        contacts: freelancer.card ? mapContacts(freelancer.card.contacts) : undefined,
        freelancerScore: Number(freelancer.rating),
        fullName: freelancer.fio,
        status: freelancer.status,
        certificates: freelancer.certificates.map(mapCertificates),
        mainSkill: mapSkills(freelancer.mainSkill!),
        skills: freelancer.skills.map(mapSkills),
        hasPublicCard: Boolean(freelancer.card),
        freelancerFeedbackItems: (freelancer.feedbacks || []).map(item => mapFreelancerFeedbackItem(item)),
        region: freelancer.region ? freelancer.region.translation : { region: '', country: '' },
        quality: {
            qualityRank: 0,
            qualityScore: 0,
            totalFreelancersCount: 0
        }
    };
}

export function mapFreelancerPrivateData({
    freelancersList,
    constants
}: FreelancerRegisteredQuery): UpdateFreelancerCardPrivateDataActionParams | undefined {
    const [freelancer] = freelancersList.filter(item => item);

    if (!freelancer || !freelancer.privateData || !freelancer.privateData.newestCard) {
        return;
    }

    const { newestCard: card, advQuality: quality } = freelancer.privateData;

    return {
        cardModerationStatus: card.statusModerate,
        about: card.briefInfo,
        avatarLink: card.avatarUrl,
        contacts: mapContacts(card.contacts),
        quality: quality
            ? {
                  qualityRank: Number(quality.advQualityRank),
                  qualityScore: Number(quality.advQualityRating),
                  totalFreelancersCount: Number(constants.freelancersCount)
              }
            : undefined
    };
}

export function mapFreelancerCustomerData({
    client,
    freelancersList
}: FreelancerRegisteredQuery): UpdateFreelancerCustomerDataActionParams {
    const { avatarUrl, name, phone } = client.info.chiefUser;
    const [project] = client.freelancerProjects;
    const [freelancer] = freelancersList;

    return {
        customer: {
            name,
            phone: getValueFromApiFormat(phone),
            avatarLink: avatarUrl,
            hasCooperation: Boolean(client.hasCooperation.length),
            isDirectOwnCustomer: client.access.canRequestFlService,
            freelancerProject: project
                ? {
                      projectId: String(project.id),
                      ownFreelancerId: String(project.freelancerId),
                      ownFreelancerLogin: project.freelancer!.login,
                      clientStatus: project.status,
                      createdTime: project.createdTime,
                      startedTime: getValueFromApiFormat(project.startedTime)
                  }
                : undefined
        },
        hasPublicCard: freelancer ? Boolean(freelancer.card) : false,
        feedbacks: ((freelancer && freelancer.feedbacks) || []).map(item => mapFreelancerFeedbackItem(item))
    };
}

export function mapFreelancerFeedbackItem(item: FreelancerFeedbackQueryFragment): FreelancerFeedbackItem {
    return {
        id: item.feedbackId,
        freelancerScore: parseFloat(item.overallMark),
        partnershipDuration: '', // TODO: разобраться с этим полем DIRECT-84131
        feedbackText: item.feedbackText,
        creationTime: item.createdTime,
        customer: item.authorInfo
            ? {
                  name: item.authorInfo.name,
                  avatar: item.authorInfo.avatarUrl,
                  login: item.authorInfo.login
              }
            : undefined,
        authorId: item.authorUid,
        willRecommend: Boolean(item.willRecommend),
        moderationStatus: item.moderationStatus
    };
}

export function mapFreelancersListItem(item: FreelancersListItemFragment): FreelancersListItem {
    const freelancerId = String(item.freelancerId);
    const region = item.region;

    if (!item.card) {
        throw Error('Freelancer card data is null');
    }

    return {
        freelancerId,
        isCurrentFreelancer: false,
        login: item.login,
        avatarLink: item.card.avatarUrl,
        about: item.card.briefInfo,
        fullName: item.fio,
        freelancerScore: Number(item.rating),
        feedbackCount: Number(item.feedbackCount),
        workCurrency: item.workCurrency,
        status: item.status,
        region: region ? region.translation : { region: '', country: '' },
        approximateDuration: item.mainSkill ? item.mainSkill.duration : '',
        approximatePrice: (item.mainSkill && Number(item.mainSkill!.price)) || 0,
        hasPublicCard: Boolean(item.card),
        services: item.skills.map(({ name }: FreelancersListItemFragment_skills) => name),
        statusModerate: GdFreelancersCardStatusModerate.ACCEPTED
    };
}

export function mapFreelacersListPrivateItem(
    item: FreelancerPrivateDataFragment_newestCard
): UpdateSingleFreelancerDataActionParams {
    return {
        about: item.briefInfo,
        avatarLink: item.avatarUrl,
        statusModerate: item.statusModerate,
        contacts: mapContacts(item.contacts)
    };
}

function mapCertificates(cert: { certId: string; type: string; link: string }): FreelancersCertificates {
    return {
        id: cert.certId,
        type: FreelancersCertificatesTypes[cert.type],
        link: cert.link
    };
}

function mapSkills(skill: {
    name: string;
    duration: GdFreelancerSkillOfferDuration;
    priceMoney: {
        valueAsFloat: number;
    };
}): FreelancerSkills {
    return {
        skillName: skill && skill.name,
        price: skill && skill.priceMoney.valueAsFloat,
        duration: skill && GdFreelancerSkillOfferDuration[skill.duration]
    };
}

function mapContacts(contact: FreelancerContactsFragment): FreelancerContacts {
    return {
        PHONE: contact.phone,
        MAIL: contact.email,
        ICQ: contact.icq || '',
        WHATSAPP: contact.whatsApp || '',
        TELEGRAM: contact.telegram || '',
        WEBSITE: contact.siteUrl || '',
        VIBER: contact.viber || '',
        SKYPE: contact.skype || ''
    };
}

function mapContactsForEditor(contact: FreelancerContactsFragment): FreelancerContactInputs {
    return {
        [FreelancerMandatoryContactTypes.PHONE]: { inputVal: contact.phone },
        [FreelancerMandatoryContactTypes.MAIL]: { inputVal: contact.email },
        [FreelancerOptionalContactTypes.ICQ]: contact.icq ? { inputVal: contact.icq } : undefined,
        [FreelancerOptionalContactTypes.WHATSAPP]: contact.whatsApp ? { inputVal: contact.whatsApp } : undefined,
        [FreelancerOptionalContactTypes.TELEGRAM]: contact.telegram ? { inputVal: contact.telegram } : undefined,
        [FreelancerOptionalContactTypes.WEBSITE]: contact.siteUrl ? { inputVal: contact.siteUrl } : undefined,
        [FreelancerOptionalContactTypes.VIBER]: contact.viber ? { inputVal: contact.viber } : undefined,
        [FreelancerOptionalContactTypes.SKYPE]: contact.skype ? { inputVal: contact.skype } : undefined
    };
}

function mapServicesForEditor(skill: {
    price: string;
    skillId: string;
    duration: GdFreelancerSkillOfferDuration;
}): FreelancerServiceInput {
    return {
        skillId: String(skill.skillId),
        price: {
            inputVal: skill.price.toString(),
            numVal: parseFloat(skill.price)
        },
        duration: {
            inputVal: skill.duration
        }
    };
}

export function mapCustomersFromQueryFormat(queryResult: FreelancerCustomersQuery): FreelancerCustomersData {
    const [customersData] = queryResult.freelancersList;

    if (!customersData) {
        throw new FreelancerAccessError(
            'No freelancer сustomers data provided',
            FreelancerAccessErrorTypes.NO_DATA_PROVIDED
        );
    }

    if (!customersData.privateData) {
        throw new FreelancerAccessError('No access to freelancer private data', FreelancerAccessErrorTypes.NO_ACCESS);
    }

    const { projects } = customersData.privateData;

    const items = projects.map(project => ({
        // project
        id: project.id,
        projectId: project.id,
        status: project.status,
        createdTime: project.createdTime,
        startedTime: project.startedTime || undefined,
        updatedTime: project.updatedTime,

        // client
        fio: project.client.fio,
        clientId: project.client.clientId,
        chiefLogin: project.client.chiefLogin,
        isDisabled: false,
        phoneNumber: getValueFromApiFormat(project.client.phoneNumber)
    }));

    const active = items.filter(item => item.status === GdFreelancerProjectStatus.IN_PROGRESS);

    const requested = items.filter(item => item.status === GdFreelancerProjectStatus.NEW);

    const canceled = items.filter(
        item =>
            item.status === GdFreelancerProjectStatus.CANCELLED_BY_FREELANCER ||
            item.status === GdFreelancerProjectStatus.CANCELLED_BY_CLIENT
    );

    return { active, canceled, requested };
}

export function mapCustomerProject(
    isAccepted: boolean,
    data: {
        freelancerProject: {
            id: string;
            updatedTime: string;
        };
    }
): UpdateCustomerRequestData {
    return {
        isAccepted,
        projectId: data.freelancerProject.id,
        updatedTime: data.freelancerProject.updatedTime
    };
}

export function mapCloseProjectByFreelancer(data: {
    freelancerProject: {
        id: string;
        updatedTime: string;
    };
}): UpdateActiveProjectData {
    return {
        projectId: data.freelancerProject.id,
        updatedTime: data.freelancerProject.updatedTime
    };
}

export function mapClientFreelancer(data: ClientFreelancerQuery): ClientFreelancerData {
    const [freelancerProject] = data.client.freelancerProjects;
    if (!freelancerProject || freelancerProject.status !== GdFreelancerProjectStatus.IN_PROGRESS) {
        throw new Error('empty clientFreelancerData');
    }
    const { freelancer } = freelancerProject;
    if (!freelancer) {
        throw new Error('empty clientFreelancerData');
    }
    return {
        login: freelancer.login,
        avatarLink: freelancer.card ? freelancer.card.avatarUrl : '',
        fullName: freelancer.fio,
        phone: freelancer.card ? freelancer.card.contacts.phone : ''
    };
}

export function mapFreelancerClient(data: ClientFreelancerQuery): ClientFreelancerData {
    const {
        client: {
            chiefLogin,
            info: { chiefUser }
        }
    } = data;
    return {
        login: chiefLogin,
        avatarLink: chiefUser.avatarUrl,
        fullName: chiefUser.name,
        phone: getValueFromApiFormat(chiefUser.phone)
    };
}

export function mapRequestFlServiceByClientResponse(
    response: RequestFlServiceByClientMutation
): RequestCooperationByClientSuccessActionParams {
    const data = response.requestFlServiceByClient;
    const { freelancerId, clientId, freelancerProject } = data;
    const { id: projectId, createdTime, startedTime, freelancer } = freelancerProject;

    if (!freelancer) {
        throw Error('freelancer must be defined');
    }

    return {
        freelancerId: String(freelancerId),
        clientId: String(clientId),
        projectId: String(projectId),
        projectCreatedTime: createdTime,
        projectStartedTime: getValueFromApiFormat(startedTime),
        projectFreelancerLogin: freelancer.login,
        clientStatus: GdFreelancerProjectStatus.NEW
    };
}

export function mapInitialFreelancerInfoResponse(
    response: FreelancerEditorQuery
): {
    editData: FreelancerCardEditData;
    servicesList: FreelancerServiceFragment[];
    certificates: FreelancersCertificates[];
    initRegion: FreelancerCardInitRegion;
} {
    const [freelancer] = response.freelancersList;
    if (!freelancer) {
        throw new FreelancerAccessError('No freelancer data provided', FreelancerAccessErrorTypes.NO_DATA_PROVIDED);
    }

    if (!freelancer.privateData) {
        throw new FreelancerAccessError('No access to private data', FreelancerAccessErrorTypes.NO_ACCESS);
    }

    const cardData: FreelancerCardEditData = {
        avatarUrl: '',
        fullName: freelancer.fio,
        registrationDate: freelancer.createTime,
        briefInfo: {
            inputVal: ''
        },
        contacts: {
            [FreelancerMandatoryContactTypes.PHONE]: {
                inputVal: ''
            },
            [FreelancerMandatoryContactTypes.MAIL]: {
                inputVal: ''
            },
            [FreelancerOptionalContactTypes.ICQ]: {
                inputVal: ''
            },
            [FreelancerOptionalContactTypes.WHATSAPP]: {
                inputVal: ''
            },
            [FreelancerOptionalContactTypes.TELEGRAM]: {
                inputVal: ''
            },
            [FreelancerOptionalContactTypes.WEBSITE]: {
                inputVal: ''
            }
        },
        services: []
    };

    const secret = freelancer.privateData.newestCard;
    const region = freelancer.region;

    if (secret) {
        cardData.avatarUrl = secret.avatarUrl;
        cardData.briefInfo.inputVal = secret.briefInfo;
        cardData.contacts = mapContactsForEditor(secret.contacts);
        cardData.services = freelancer.skills.map(mapServicesForEditor);
    }

    return {
        editData: cardData,
        servicesList: response.constants.freelancerSkillTypes.map(skillType => ({
            ...skillType,
            skillId: String(skillType.skillId)
        })),
        certificates: freelancer.certificates.map(mapCertificates),
        initRegion: {
            geoId: region ? Number(region.regionId) : undefined,
            region: region ? region.translation.region : '',
            country: region ? region.translation.country : ''
        }
    };
}

export function mapStateForFreelancerServicesMutation(
    editData: FreelancerCardEditData
): GdUpdateFreelancerSkillItemInput[] {
    return editData.services.map(service => ({
        price: (service.price.numVal || 0).toString(),
        days: service.duration.inputVal,
        skillId: service.skillId.toString()
    }));
}

export function mapStateForFreelancerCardMutation(
    editData: FreelancerCardEditData,
    avatarId?: string
): GdUpdateFreelancerCardItemInput {
    const contacts = {
        email: editData.contacts.MAIL.inputVal,
        phone: editData.contacts.PHONE.inputVal,
        icq: editData.contacts.ICQ && editData.contacts.ICQ.inputVal,
        telegram: editData.contacts.TELEGRAM && editData.contacts.TELEGRAM.inputVal,
        whatsApp: editData.contacts.WHATSAPP && editData.contacts.WHATSAPP.inputVal,
        siteUrl: editData.contacts.WEBSITE && editData.contacts.WEBSITE.inputVal,
        viber: editData.contacts.VIBER && editData.contacts.VIBER.inputVal,
        skype: editData.contacts.SKYPE && editData.contacts.SKYPE.inputVal
    };

    // если avatarId нет, аватарка не меняется.
    return {
        briefInfo: editData.briefInfo.inputVal,
        contacts,
        avatarId
    };
}

export function mapPublicApiOperator(
    operator: FreelancerPublicQuery_operator | null
): FreelancerCardOperator | undefined {
    return operator
        ? {
              login: operator.login,
              clientId: getValueFromApiFormat(operator.clientId)
          }
        : undefined;
}

export function mapLangToGdLanguageInput(lang: Lang): GdLanguageInput {
    const languages: Record<Lang, GdLanguageInput> = {
        [Lang.ru]: GdLanguageInput.RU,
        [Lang.en]: GdLanguageInput.EN,
        [Lang.uk]: GdLanguageInput.UK,
        [Lang.tr]: GdLanguageInput.TR
    };

    return languages[lang];
}
